import { useLayoutEffect, useRef, useState } from "react";
import type { Dispatch, SetStateAction } from "react";
import type React from "react";
import {
  type Header,
  type Row,
  type RowData,
  type Table as TableInstance,
  flexRender,
} from "@tanstack/react-table";
import { useWindowVirtualizer } from "@tanstack/react-virtual";
import type { DataTableFeatures } from "./dataTableFeatures";
import { cn } from "~/lib/utils";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "~/atoms/table";

interface DataTableProps<TData extends RowData> {
  table: TableInstance<DataTableFeatures, TData>;
  noResultsLabel: string;
  getCellOnClick?: (
    columnId: string,
    rowData: TData,
  ) => (() => void) | undefined;
  wrapperRef?: React.Ref<HTMLDivElement>;
  wrapperClassName?: string;
  virtualizeRows?: boolean;
}

const ESTIMATED_ROW_HEIGHT = 48;

interface ColumnResizeContext<TData extends RowData> {
  headerByColumnId: Map<string, Header<DataTableFeatures, TData, unknown>>;
  highlightedColumnId: string | null;
  setHoveredColumnId: Dispatch<SetStateAction<string | null>>;
}

const ColumnResizeHandle = <TData extends RowData>({
  header,
  columnResize,
}: {
  header: Header<DataTableFeatures, TData, unknown>;
  columnResize: ColumnResizeContext<TData>;
}) => {
  const columnId = header.column.id;
  const isHighlighted = columnResize.highlightedColumnId === columnId;
  return (
    <div
      onMouseDown={header.getResizeHandler()}
      onTouchStart={header.getResizeHandler()}
      onDoubleClick={() => header.column.resetSize()}
      onClick={(event) => event.stopPropagation()}
      onMouseEnter={() => columnResize.setHoveredColumnId(columnId)}
      onMouseLeave={() =>
        columnResize.setHoveredColumnId((current) =>
          current === columnId ? null : current,
        )
      }
      className="absolute top-0 right-0 -bottom-px w-4 cursor-col-resize touch-none select-none"
    >
      <div
        className={cn(
          "absolute top-0 right-0 -bottom-px",
          isHighlighted ? "bg-border w-0.5" : "bg-border w-px",
        )}
      />
    </div>
  );
};

const renderCells = <TData extends RowData>(
  row: Row<DataTableFeatures, TData>,
  getCellOnClick: DataTableProps<TData>["getCellOnClick"],
  columnResize: ColumnResizeContext<TData>,
) =>
  row.getVisibleCells().map((cell) => {
    const onClick = getCellOnClick?.(cell.column.id, row.original);
    const resizeHeader = columnResize.headerByColumnId.get(cell.column.id);
    return (
      <TableCell
        key={cell.id}
        className={cn(
          "relative",
          onClick && "hover:bg-muted cursor-pointer transition-colors",
        )}
        onClick={onClick}
      >
        <div className="overflow-hidden text-ellipsis">
          {flexRender(cell.column.columnDef.cell, cell.getContext())}
        </div>
        {resizeHeader && (
          <ColumnResizeHandle
            header={resizeHeader}
            columnResize={columnResize}
          />
        )}
      </TableCell>
    );
  });

export const DataTable = <TData extends RowData>({
  table,
  noResultsLabel,
  getCellOnClick,
  wrapperRef,
  wrapperClassName,
  virtualizeRows = false,
}: DataTableProps<TData>) => {
  const rows = table.getRowModel().rows;
  const colCount = table.getVisibleLeafColumns().length;
  const [hoveredColumnId, setHoveredColumnId] = useState<string | null>(null);
  const resizableHeaders = table
    .getHeaderGroups()
    .flatMap((headerGroup) => headerGroup.headers)
    .filter((header) => header.column.getCanResize());
  const resizingColumnId =
    resizableHeaders.find((header) => header.column.getIsResizing())?.column
      .id ?? null;
  const columnResize: ColumnResizeContext<TData> = {
    headerByColumnId: new Map(
      resizableHeaders.map((header) => [header.column.id, header]),
    ),
    highlightedColumnId: resizingColumnId ?? hoveredColumnId,
    setHoveredColumnId,
  };

  return (
    <Table
      className="bg-card border-border min-w-full table-fixed border"
      style={{ width: table.getTotalSize() }}
      wrapperRef={wrapperRef}
      wrapperClassName={wrapperClassName}
    >
      <TableHeader className="bg-muted">
        {table.getHeaderGroups().map((headerGroup) => (
          <TableRow key={headerGroup.id}>
            {headerGroup.headers.map((header) => (
              <TableHead
                key={header.id}
                className="relative"
                style={{ width: header.getSize() }}
              >
                <div
                  className="overflow-hidden text-ellipsis"
                  title={header.column.columnDef.meta?.label}
                >
                  {header.isPlaceholder
                    ? null
                    : flexRender(
                        header.column.columnDef.header,
                        header.getContext(),
                      )}
                </div>
                {header.column.getCanResize() && (
                  <ColumnResizeHandle
                    header={header}
                    columnResize={columnResize}
                  />
                )}
              </TableHead>
            ))}
          </TableRow>
        ))}
      </TableHeader>
      {virtualizeRows && rows.length > 0 ? (
        <VirtualizedTableBody
          rows={rows}
          getCellOnClick={getCellOnClick}
          colCount={colCount}
          columnResize={columnResize}
        />
      ) : (
        <TableBody>
          {rows.length ? (
            rows.map((row) => (
              <TableRow key={row.id}>
                {renderCells(row, getCellOnClick, columnResize)}
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell colSpan={colCount} className="h-24 text-center">
                {noResultsLabel}
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      )}
    </Table>
  );
};

interface VirtualizedTableBodyProps<TData extends RowData> {
  rows: Row<DataTableFeatures, TData>[];
  getCellOnClick: DataTableProps<TData>["getCellOnClick"];
  colCount: number;
  columnResize: ColumnResizeContext<TData>;
}

const VirtualizedTableBody = <TData extends RowData>({
  rows,
  getCellOnClick,
  colCount,
  columnResize,
}: VirtualizedTableBodyProps<TData>) => {
  const tbodyRef = useRef<HTMLTableSectionElement>(null);
  const [scrollMargin, setScrollMargin] = useState(0);

  useLayoutEffect(() => {
    const node = tbodyRef.current;
    if (!node) return;
    setScrollMargin(node.getBoundingClientRect().top + window.scrollY);
  }, [rows.length]);

  const virtualizer = useWindowVirtualizer({
    count: rows.length,
    estimateSize: () => ESTIMATED_ROW_HEIGHT,
    overscan: 8,
    scrollMargin,
  });

  const virtualItems = virtualizer.getVirtualItems();
  const totalSize = virtualizer.getTotalSize();
  const firstItem = virtualItems[0];
  const lastItem = virtualItems[virtualItems.length - 1];
  const paddingTop = firstItem ? firstItem.start - scrollMargin : 0;
  const paddingBottom = lastItem ? totalSize - lastItem.end : 0;

  return (
    <TableBody ref={tbodyRef}>
      {paddingTop > 0 && (
        <tr aria-hidden>
          <td
            colSpan={colCount}
            style={{ height: paddingTop, padding: 0, border: 0 }}
          />
        </tr>
      )}
      {virtualItems.map((virtualItem) => {
        const row = rows[virtualItem.index];
        if (!row) return null;
        return (
          <TableRow
            key={row.id}
            ref={virtualizer.measureElement}
            data-index={virtualItem.index}
          >
            {renderCells(row, getCellOnClick, columnResize)}
          </TableRow>
        );
      })}
      {paddingBottom > 0 && (
        <tr aria-hidden>
          <td
            colSpan={colCount}
            style={{ height: paddingBottom, padding: 0, border: 0 }}
          />
        </tr>
      )}
    </TableBody>
  );
};
